// Landing page for Audio Annotation Tool (for annotating emergency vehicle sounds such as sirens, horns, etc. and maybe more in the future)

import { FC, MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import { WavesurferRow } from 'packages/main/components/audio-tool/audio-tool-wavesurfer-row';
import { Layout, Spin } from 'antd';
import {
  AudioAnnotationTypes,
  getAnnotationTypeFromCategoryName,
} from 'packages/main/components/audio-tool/audio-tool-types';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  audioExtractionTokenAtom,
  curAnnotationTypesAtom,
} from 'packages/main/components/audio-tool/atoms';
import { AudioToolControls } from 'packages/main/components/audio-tool/audio-tool-controls';
import { stylesWR } from 'packages/main/components/audio-tool/audio-tool-wavesurfer-row';
import { useDeselectCurRegion } from 'packages/main/components/audio-tool/audio-tool-event-handlers';
import {
  useAudioAnnotationsForExtractionSWR,
  useAudioExtractionContentSWR,
  useAudioExtractionSWRToRecoil,
  useAudioTaskSWR,
  useCategoriesSWRToRecoil,
} from 'packages/main/components/audio-tool/api';
import {
  devLog,
  getSirenCategoryNameFromToken,
} from 'packages/main/components/audio-tool/utils';
import CenteredBox from 'packages/lib/common-ui/centeredBox';
import useWindowSize from 'packages/lib/utils/hooks/use-window-size';
import StateIcon, { GenericState } from 'packages/lib/common-ui/state-icon';

//import audioData from './output-4-channels.wav'; // Useful for testing purposes. Pass as audioURL={audioData} prop to WavesurferRow
import { AudioToolHeaderControls } from 'packages/main/components/audio-tool/audio-tool-header-controls';
import { taskStateZod } from 'packages/lib/api/annotations/contract/enums';

const { Header, Footer, Content } = Layout;

export const SPECTROGRAM_HEIGHT = 150;
export const AUDIO_WAVEFORM_HEIGHT = 0;
export const OTHER_AREAS_HEIGHT = 315; // Aside from the wavesurfers, height of all other content
export const TIMELINE_HEIGHT = 20;
export const TOTAL_CONTENT_HEIGHT =
  SPECTROGRAM_HEIGHT + OTHER_AREAS_HEIGHT + TIMELINE_HEIGHT;
const MIN_WAVESURFER_HEIGHT = 20;

export const IGNORE_DESELECT_CLICK_CLASS_NAME = 'IgnoreDeselectClick';

const styles: Record<string, React.CSSProperties> = {
  categorySelect: { minWidth: '400px', margin: 0, padding: 0 },
  channelSelect: { minWidth: '600px', margin: 0, padding: 0 },
  header: {
    backgroundColor: 'rgba(255,255,255, 0)',
    minHeight: '100px',
    maxHeight: '140px',
    paddingTop: '10px',
  },
  headerProgressOuterDiv: {
    display: 'grid',
    gridTemplateColumns: '0.2fr 1fr 0.2fr',
  },
  headerProgress: {
    display: 'flex',
    justifyContent: 'center',
  },
  headerCategoryAndChannelControls: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.35fr',
    justifyItems: 'stretch',
    justifyContent: 'space-between',
    minWidth: '1300px',
    paddingTop: '10px',
  },
  content: {
    padding: '20px 50px',
    height: `calc(100vh - ${OTHER_AREAS_HEIGHT - TIMELINE_HEIGHT}px)`,
    minHeight: '300px',
  },
  footer: { height: `${130}px`, margin: '0 0' },
  spectrogramContainer: { marginBottom: '16px' },
};

export const AudioToolMain: FC<React.PropsWithChildren<unknown>> = () => {
  const windowSize = useWindowSize(0);
  const windowSizeThrottled = useWindowSize(200);
  const audioExtractionToken = useRecoilValue(audioExtractionTokenAtom);
  const didSetAnnotationTypes = useRef(false);

  const { data: audioData, error: audioDataError } =
    useAudioExtractionContentSWR(audioExtractionToken);

  const { data: audioAnnotations, error: audioAnnotationsError } =
    useAudioAnnotationsForExtractionSWR(audioExtractionToken);

  const spectrogramContainerRef = useRef<HTMLDivElement | null>(null);
  const [_curAnnotationTypes, setCurAnnotationTypes] = useRecoilState(
    curAnnotationTypesAtom
  );
  const deselectCurRegion = useDeselectCurRegion();

  const { data: categories } = useCategoriesSWRToRecoil();
  const { data: audioExtraction, error: audioExtractionError } =
    useAudioExtractionSWRToRecoil(audioExtractionToken);

  const { data: task, mutate: reloadTask } =
    useAudioTaskSWR(audioExtractionToken);

  // TODO: Remove for final release. Purely for dev / debugging purposes
  useEffect(() => {
    devLog('Audio Extraction from API:', audioExtraction);
  }, [audioExtraction]);
  useEffect(() => {
    devLog(
      'Audio Annotations from API:',
      audioAnnotations,
      audioAnnotationsError
    );
  }, [audioAnnotations, audioAnnotationsError]);

  // Once annotations have loaded, set the visible categories based on what has been annotated. Do this only once.
  useEffect(() => {
    if (!didSetAnnotationTypes.current && audioAnnotations && categories) {
      const foundTypes = new Set<string>();
      for (const item of audioAnnotations) {
        if (item.categoryToken) {
          const sirenCategoryName = getSirenCategoryNameFromToken(
            item.categoryToken,
            categories
          );
          const annotationType =
            getAnnotationTypeFromCategoryName(sirenCategoryName);
          if (annotationType) {
            foundTypes.add(annotationType);
          }
        }
      }

      setCurAnnotationTypes(Array.from(foundTypes) as AudioAnnotationTypes[]);
      didSetAnnotationTypes.current = true;
    }
  }, [audioAnnotations, categories, setCurAnnotationTypes]);

  // We want to allow clicking anywhere (outside of a wavesurfer or button) to deselect the currently-selected region.
  // Buttons are inside of <span> or <button> elements and Wavesurfer is in a 'WAVE' element (or 'REGION' if user clicks in a region)
  const handleDivClick = useCallback<MouseEventHandler<HTMLElement>>(
    async (e): Promise<void> => {
      const element = e.target as HTMLElement;

      if (
        element.tagName !== 'REGION' &&
        element.tagName !== 'SPAN' &&
        element.tagName !== 'BUTTON' &&
        !element.classList.contains(IGNORE_DESELECT_CLICK_CLASS_NAME) &&
        element.closest('.' + IGNORE_DESELECT_CLICK_CLASS_NAME) === null
      ) {
        await deselectCurRegion();
      }
    },
    [deselectCurRegion]
  );

  const allAPIDataIsLoaded = !!(
    audioAnnotations &&
    audioExtraction &&
    !audioExtractionError &&
    categories
  );

  const audioExtractionTokenIsInvalid = audioExtractionToken.length < 30;
  const waveformAreaHeight =
    windowSize.height - (TOTAL_CONTENT_HEIGHT + AUDIO_WAVEFORM_HEIGHT);
  const warningIcon = <StateIcon state={GenericState.Warning} />;

  return (
    <>
      {!allAPIDataIsLoaded ? (
        <CenteredBox>
          {audioExtractionTokenIsInvalid ? (
            <div>
              The <b>audioExtractionToken </b>
              {audioExtractionToken.length === 0 ? (
                <span>is missing from the URL </span>
              ) : (
                <span>
                  in the URL does not appear to be valid: {audioExtractionToken}
                </span>
              )}
            </div>
          ) : audioExtractionError ? (
            <>
              <div style={{ justifyContent: 'center', fontWeight: 800 }}>
                The audio extraction failed to load
              </div>
              <br />
              <br />
              {audioExtractionError.message}
            </>
          ) : (
            <Spin />
          )}
        </CenteredBox>
      ) : (
        <Layout className="layout" onClick={handleDivClick}>
          <Header
            style={styles.header}
            onClick={async (e): Promise<void> => {
              await deselectCurRegion();
            }}
          >
            <AudioToolHeaderControls task={task} reloadTask={reloadTask} />
          </Header>
          <Content style={styles.content}>
            {audioData ? (
              <>
                <div style={stylesWR.wavesurferRow}>
                  <div style={stylesWR.rowLabel}>Spectrogram</div>
                  <div
                    style={styles.spectrogramContainer}
                    ref={spectrogramContainerRef}
                  />
                </div>
                <WavesurferRow
                  annotationType={AudioAnnotationTypes.NoAnnotations}
                  audioAnnotations={audioAnnotations}
                  audioBlob={audioData}
                  mute={false}
                  showTimeline={true}
                  spectrogramContainerRef={spectrogramContainerRef}
                  height={
                    _curAnnotationTypes.length === 0
                      ? waveformAreaHeight / 2
                      : AUDIO_WAVEFORM_HEIGHT
                  }
                  width={windowSizeThrottled.width}
                />
                {_curAnnotationTypes.map((annotationType, idx) => (
                  <WavesurferRow
                    annotationType={annotationType}
                    audioAnnotations={audioAnnotations}
                    audioBlob={audioData}
                    mute={true}
                    showTimeline={false}
                    key={'waveform-' + annotationType}
                    height={Math.max(
                      waveformAreaHeight / _curAnnotationTypes.length,
                      MIN_WAVESURFER_HEIGHT
                    )}
                  />
                ))}
              </>
            ) : audioDataError ? (
              <span>
                Error: Could not load audio <br />
                <br /> {audioDataError.message}
              </span>
            ) : (
              <CenteredBox>
                <Spin />
                Loading audio...
              </CenteredBox>
            )}
            {_curAnnotationTypes.length === 0 && (
              <div style={{ height: waveformAreaHeight / 2 + 'px' }}>
                <CenteredBox>
                  {task && (
                    <>
                      {task.state === taskStateZod.enum.Rejected ? (
                        <span style={{ color: 'red', fontWeight: 800 }}>
                          {warningIcon} This audio clip was rejected. Reason:{' '}
                          {task.comments}
                        </span>
                      ) : task.state === taskStateZod.enum.ToDo ? (
                        <span>
                          {' '}
                          Step 1: Listen to the audio clip, then add Siren
                          Categories that are present in the audio using the
                          control at the top of the screen. Then drag to
                          annotate siren sounds.
                        </span>
                      ) : (
                        <span>
                          This audio clip was annotated as containing no siren
                          sounds.
                        </span>
                      )}
                    </>
                  )}
                </CenteredBox>
              </div>
            )}
          </Content>
          {audioData && (
            <Footer style={styles.footer}>
              <AudioToolControls />
            </Footer>
          )}
        </Layout>
      )}
    </>
  );
};

export default AudioToolMain;
